import { useEffect, useCallback } from "react";
import Conversation, { type ReviewStatus } from "admin/types/interfaces/conversation";
import { useMutator } from "~/hooks/useFetcher";

const { VITE_ADMIN_CONVERSATIONS_API } = import.meta.env;

export type BinaryFeedbackFilter = "POSITIVE" | "NEGATIVE";
export type ImpactFilter = "SPECIFIED" | "NONE" | "POSITIVE" | "NEGATIVE";

interface AdminConversationSearchRequest {
  user_id?: string;
  agent_id?: string;
  app_id?: string;
  project_id?: string;
  dataset_name?: string;
  binary_feedback_filter?: BinaryFeedbackFilter[];
  has_annotations?: boolean;
  has_comments?: boolean;
  impact_filter?: ImpactFilter[];
  review_status?: ReviewStatus[];
}

interface ConversationListResponse {
  items: Conversation[];
  limit?: number;
  offset?: number;
  total?: number;
}

export type SortKey = "updated_at" | "created_at" | "number_of_annotations";
export type SortOrder = "asc" | "desc";

interface UseConversationsParams {
  limit?: number;
  offset?: number;
  userId?: string;
  agentId?: string;
  appId?: string;
  projectId?: string;
  datasetName?: string;
  feedbackFilter?: BinaryFeedbackFilter[];
  hasAnnotations?: boolean | null;
  hasComments?: boolean | null;
  impactFilter?: ImpactFilter[];
  reviewStatus?: ReviewStatus[];
  sortKey?: SortKey;
  sortOrder?: SortOrder;
}

/**
 * Custom hook for fetching conversations with pagination and filtering.
 *
 * Filters with OR semantics (binary_feedback_filter, impact_filter, review_status)
 * accept arrays; empty arrays are treated as "no filter" and dropped from the request.
 */
const useConversations = ({
  limit,
  offset = 0,
  userId,
  agentId,
  appId,
  projectId,
  datasetName,
  feedbackFilter,
  hasAnnotations,
  hasComments,
  impactFilter,
  reviewStatus,
  sortKey = "updated_at",
  sortOrder = "desc",
}: UseConversationsParams = {}) => {
  const buildUrl = () => {
    const params = new URLSearchParams();

    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }
    if (offset !== undefined) {
      params.append("offset", offset.toString());
    }
    if (sortKey) {
      params.append("sort_key", sortKey);
    }
    if (sortOrder) {
      params.append("sort_order", sortOrder);
    }

    const queryString = params.toString();
    return `${VITE_ADMIN_CONVERSATIONS_API}/search${queryString ? `?${queryString}` : ""}`;
  };

  const currentUrl = buildUrl();

  const {
    data,
    error,
    isMutating: isLoading,
    trigger,
  } = useMutator<ConversationListResponse, AdminConversationSearchRequest>(currentUrl, {
    requestInit: {
      method: "POST",
    },
    shouldCamelize: true,
  });

  const search = useCallback(
    async (searchRequest: AdminConversationSearchRequest = {}) => {
      return await trigger(searchRequest);
    },
    [trigger]
  );

  useEffect(() => {
    const searchRequest: AdminConversationSearchRequest = {};
    if (userId) {
      searchRequest.user_id = userId;
    }
    if (agentId) {
      searchRequest.agent_id = agentId;
    }
    if (appId) {
      searchRequest.app_id = appId;
    }
    if (projectId) {
      searchRequest.project_id = projectId;
    }
    if (datasetName) {
      searchRequest.dataset_name = datasetName;
    }
    if (feedbackFilter?.length) {
      searchRequest.binary_feedback_filter = feedbackFilter;
    }
    if (hasAnnotations !== undefined && hasAnnotations !== null) {
      searchRequest.has_annotations = hasAnnotations;
    }
    if (hasComments !== undefined && hasComments !== null) {
      searchRequest.has_comments = hasComments;
    }
    if (impactFilter?.length) {
      searchRequest.impact_filter = impactFilter;
    }
    if (reviewStatus?.length) {
      searchRequest.review_status = reviewStatus;
    }
    search(searchRequest);
  }, [
    limit,
    offset,
    userId,
    agentId,
    appId,
    projectId,
    datasetName,
    feedbackFilter,
    hasAnnotations,
    hasComments,
    impactFilter,
    reviewStatus,
    sortKey,
    sortOrder,
    search,
  ]);

  return {
    data: data?.items || [],
    total: data?.total,
    limit: data?.limit,
    offset: data?.offset,
    error,
    isLoading,
    isValidating: isLoading,
    mutate: search,
  };
};

export default useConversations;
